/**
 * Cookie consent store.
 *
 * FaceWise loads no analytics or marketing scripts today, so every visitor's
 * choice is currently honoured trivially. The store exists anyway so that the
 * day we do add one, the gate is already in place and a stored "reject" means
 * something: nothing non-essential may load without asking {@link hasConsent}
 * first.
 */

/** Everything except `necessary` is opt-in and defaults to off. */
export type ConsentCategory = 'necessary' | 'analytics' | 'marketing'

/** The optional categories, in the order the banner lists them. */
export const OPTIONAL_CATEGORIES: readonly Exclude<ConsentCategory, 'necessary'>[] = [
  'analytics',
  'marketing',
]

export type CookieConsent = {
  analytics: boolean
  marketing: boolean
  /** ISO timestamp of the decision — what we would show if anyone ever asks. */
  decidedAt: string
  /** Bump when the categories change, so stale decisions are re-asked. */
  version: number
}

/** Current wording/category set. Bump to re-ask everyone. */
export const CONSENT_VERSION = 1

const STORAGE_KEY = 'facewise_cookie_consent'

/**
 * The stored decision, or `null` when the visitor has not decided yet (or the
 * decision predates the current {@link CONSENT_VERSION}). Reading localStorage
 * can throw in a locked-down browser — treat that as "not decided".
 */
export function readConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<CookieConsent>
    if (parsed.version !== CONSENT_VERSION) return null
    return {
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      decidedAt: typeof parsed.decidedAt === 'string' ? parsed.decidedAt : '',
      version: CONSENT_VERSION,
    }
  } catch {
    return null
  }
}

/** Persist a decision. Returns what was stored so callers can react to it. */
export function writeConsent(choice: { analytics: boolean; marketing: boolean }): CookieConsent {
  const consent: CookieConsent = {
    analytics: choice.analytics,
    marketing: choice.marketing,
    decidedAt: new Date().toISOString(),
    version: CONSENT_VERSION,
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
  } catch {
    // Private mode: the banner comes back next visit, which is the safe default.
  }
  return consent
}

/**
 * The gate every non-essential script must pass. `necessary` is always true —
 * without it there is no session and no app.
 */
export function hasConsent(category: ConsentCategory): boolean {
  if (category === 'necessary') return true
  const consent = readConsent()
  return consent ? consent[category] : false
}
